import { useCallback, useContext, useState } from "react";
import { Colors } from "../../../utils/colors";
import {
  censoredWordsDetected,
  detectedCensored,
  isApprovedPhoneStatus,
  MARKIT_FREE_CREATOR_NUMBER,
  massTextMessageConverter,
  PhoneErrorType,
} from "@markit/common.utils";
import { API } from "../../../API";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import PopupModalContainer from "../../Containers/PopupModalContainer";
import RectangleButton from "../../Buttons/RectangleButton";
import AlertButtonWrapper from "../../Containers/AlertButtonWrapper";
import { generate } from "shortid";
import { saveMediaToFirestore } from "../../../utils/photoUtils";
import CustomPhoneTextField from "../../TextFields/CustomPhoneTextField";
import { showPhoneInputError } from "../../../utils/phoneUtils";
import { Event, FollowerStatus, MassText } from "@markit/common.types";
import { fetchFollowerDataFromPhoneNumber } from "../../../utils/userUtils";
import { unsubscribedUserAlertText } from "../../../utils/alertUtils";
import FatHeaderActionButton from "../../Buttons/FatHeaderActionButton";
import { getMassTextAttachments } from "../../../utils/textingUtils";
import { MixpanelContext } from "../../../context/AnalyticsService";
import AlertContainer from "../../Containers/AlertContainer";

type TestTextButtonProps = {
  massText: MassText;
  event?: Event;
  image?: string;
  save?: boolean; // only save if it's an image not already saved in firebase
};

const TestTextButton = (props: TestTextButtonProps) => {
  const { massText, event, image, save } = props;
  const { account } = useSelector(getAccountState);
  const { accountData } = account;
  const mixpanel = useContext(MixpanelContext);
  const [isVisible, setIsVisible] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState<PhoneErrorType>({
    isError: false,
    message: "",
  });
  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });
  const [mainAlertText, setMainAlertText] = useState({
    heading: "",
    subHeading: "",
  });
  const [sending, setSending] = useState(false);

  const styles = {
    subtext: {
      fontSize: 14,
      color: Colors.GRAY1,
    },
  };

  const testMessageOnPress = useCallback(() => {
    if (!massText.message) {
      setMainAlertText({
        heading: "Please write a message before testing.",
        subHeading: "",
      });
      return;
    }
    if (detectedCensored(massText.message)) {
      setMainAlertText({
        heading: "Please remove censored terms before testing.",
        subHeading:
          "Flagged words detected: " + censoredWordsDetected(massText.message),
      });
      return;
    }
    setIsVisible(true);
  }, [massText.message]);

  const sendingTextError = useCallback(
    (error: any) => {
      console.log("sendingTestTextError: ", error.message);
      setAlertText({
        heading: "An error occurred.",
        subHeading:
          "Please contact our hotline 617-213-0897 to diagnose the issue before attempting to send again.",
      });
      mixpanel.track("Mass Text Message Failed", {
        message: massText.message,
        recipientsLength: 1,
        errorMessage: error.message,
        test_text: true,
      });
    },
    [massText.message, mixpanel]
  );

  const receiveTestMessage = useCallback(
    async (phoneNumber: string) => {
      const massTextAttachments = massText.campaignId
        ? await getMassTextAttachments(accountData.uid, massText)
        : [];
      const finalMessage: string = massTextMessageConverter(
        massText.message,
        massTextAttachments,
        accountData.trustedSender
      );

      let mediaUrl = "";
      setSending(true);
      try {
        if (image && save) {
          const url = await saveMediaToFirestore(
            image,
            accountData.uid + "/textMedia/tests/" + generate(),
            accountData.customer.contactCardUrl
          );
          mediaUrl = url;
        } else if (image) {
          mediaUrl = image;
        }

        await API.text
          .sendTestMessage({
            userId: accountData.uid,
            message: finalMessage,
            mediaUrl: mediaUrl,
            event: event,
            fromPhoneNumber: isApprovedPhoneStatus(accountData)
              ? accountData.customer.phone
              : MARKIT_FREE_CREATOR_NUMBER,
            toPhoneNumber: phoneNumber,
          })
          .then((response) => {
            if (response.success) {
              setAlertText({ heading: "Sent!", subHeading: "" });
            } else {
              sendingTextError(
                new Error("An error occurred when sending the text")
              );
            }
          });
      } catch (error: any) {
        sendingTextError(error);
      } finally {
        setSending(false);
      }
    },
    [massText, accountData, image, save, event, sendingTextError]
  );

  const showInputOrSend = useCallback(async () => {
    if (showPhoneInputError(testPhoneNumber, setPhoneError)) {
      return;
    }
    const followerData = await fetchFollowerDataFromPhoneNumber(
      accountData.uid,
      testPhoneNumber
    );

    if (followerData && followerData.status === FollowerStatus.UNSUBSCRIBED) {
      setAlertText({
        heading: unsubscribedUserAlertText(followerData),
        subHeading:
          "Text START to " +
          accountData.customer.phone +
          " and send test message again.",
      });
    } else {
      receiveTestMessage(testPhoneNumber);
    }
  }, [
    accountData.customer.phone,
    accountData.uid,
    receiveTestMessage,
    testPhoneNumber,
  ]);

  return (
    <>
      <div>
        <FatHeaderActionButton
          title="Test"
          onPress={testMessageOnPress}
          containerStyles={{ paddingInline: 14, paddingBlock: 10 }}
        />
      </div>
      {isVisible ? (
        <PopupModalContainer
          headerComp="Test Text"
          valueComp={
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <div className="ColumnNormal" style={{ gap: 4 }}>
                <span className="AboutSubtitle">What is a test text?</span>
                <div className="ColumnNormal" style={{ gap: 6 }}>
                  <span style={styles.subtext}>
                    A test text allows you to preview your text in your native
                    operating system, so you can see how it will look to your
                    attendees.
                  </span>
                  <span style={styles.subtext}>
                    Any name related plugin will be converted to your own name.
                  </span>
                  <span style={styles.subtext}>
                    External links will not be auto-converted to Markit links in
                    the test text.
                  </span>
                </div>
              </div>
              <div className="ColumnNormal" style={{ gap: 4, marginTop: 7 }}>
                <span className="AboutSubtitle">Select Recipient</span>
                <span style={styles.subtext}>
                  This test text will only be sent to one recipient. Enter the
                  phone number you want this test text to be sent to below.
                </span>
              </div>
              <CustomPhoneTextField
                phoneNumber={testPhoneNumber}
                onPhoneChange={(value) => setTestPhoneNumber(value)}
                showPhoneError={phoneError}
                placeholder="Enter Phone Number"
              />
              <AlertButtonWrapper
                buttonComp={
                  <RectangleButton
                    buttonLabel="Send Test Text"
                    onPress={showInputOrSend}
                    altTextColor={Colors.WHITE}
                    altColor={Colors.BLACK}
                    loading={sending}
                  />
                }
                alertTextHeader={alertText.heading}
                alertTextSubHeader={alertText.subHeading}
                clearAlert={() => {
                  if (
                    alertText.heading === "Sent!" &&
                    alertText.subHeading === ""
                  ) {
                    setIsVisible(false);
                  }
                  setAlertText({ heading: "", subHeading: "" });
                }}
              />
            </div>
          }
          closeModal={() => {
            setIsVisible(false);
          }}
        />
      ) : null}
      <AlertContainer
        headerComp={mainAlertText.heading}
        subHeaderComp={mainAlertText.subHeading}
        closeModal={() => setMainAlertText({ heading: "", subHeading: "" })}
        hideModal={mainAlertText.heading === ""}
      />
    </>
  );
};

export default TestTextButton;
